import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import defaultStyles from './SequenceTable.module.css';
import jlrStyles from './SequenceTableJlr.module.css';
import VirtualizedTable from '../../components/VirtualizedTable/VirtualizedTable';
import { getSelectedOptionalColumns } from '../../utils/tableUtils';
import HeaderSearchBar from '../../components/HeaderSearchBar.js/HeaderSearchBar';
import {
  optionalColumnsWithShift as defaultOptionalColumns,
  optionalColumnsExceptCv,
  optionalColumnsForPVWithShift,
} from '../../staticData';
import VirtualizedDragAndDropList from '../../components/VirtualizedDragAndDropList/VirtualizedDragAndDropList';
import { APPLICATION_VARIANTS, JLR_PLANT } from '../../constants';
import config from '../../config/config';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { setStylesAccordingToPlant } from '../../utils/utils';

const SequenceTable = ({
  enableRowsSelection,
  showRowNumbers,
  enableDragAndDrop,
  sourceRows,
  selectedOptionalColNames,
  optionalColumns = defaultOptionalColumns,
  headerRowHeight = 38,
  rowHeight = 36,
  handleRowSelect,
  handleAllRowsSelect,
  handleDragEnd,
  classNames = {},
  renderPlaceholder,
  ...props
}) => {
  const [styles, setStyles] = useState(defaultStyles);
  const [plant] = usePlantAndLinesStore().plantState;
  const isLucknowProlifePlant = plant.label === 'Lucknow-Prolife'
  const parentName = isLucknowProlifePlant ? 'Part No.' : 'VC'
  const vcDescriptionLabel = isLucknowProlifePlant ? 'Model No.' : 'Description'

  if (enableDragAndDrop && enableRowsSelection) {
    throw new Error(
      'Both DnD and row selection are not supported at the same time. Pls enable only one of the either at once',
    );
  }

  const searchTextMapInitialState = {
    vc: '',
    description: '',
  };
  const [searchTextMap, setSearchTextMap] = useState(
    config?.APPLICATION_VARIANT === APPLICATION_VARIANTS.IPMS4
      ? optionalColumns.reduce(
          (acc, col) => ({
            ...acc,
            [col.field]: '',
          }),
          searchTextMapInitialState,
        )
      : optionalColumnsForPVWithShift.reduce(
          (acc, col) => ({
            ...acc,
            [col.field]: '',
          }),
          searchTextMapInitialState,
        ),
  );

  const [rows, setRows] = useState([...sourceRows]);

  const handleSearchTextChangeCurry = field => e => {
    const value = e.target.value;
    setSearchTextMap(prev => ({ ...prev, [field]: value }));
  };

  const clearSearchTextCurry = field => () =>
    setSearchTextMap(prev => ({ ...prev, [field]: '' }));

  const fixedColumns = [
    {
      title: parentName,
      field: 'vc',
      width: 120,
      style: { textAlign: 'left' },
      renderHeader() {
        return (
          <HeaderSearchBar
            value={searchTextMap[this.field]}
            handleChange={handleSearchTextChangeCurry(this.field)}
            title={this.title}
            placeholder={`${this.title} search`}
            clearData={clearSearchTextCurry(this.field)}
          />
        );
      },
    },
    {
      title: vcDescriptionLabel,
      field: 'description',
      width: 200,
      style: { textAlign: 'left' },
      renderHeader() {
        return (
          <HeaderSearchBar
            value={searchTextMap[this.field]}
            handleChange={handleSearchTextChangeCurry(this.field)}
            title={this.title}
            placeholder={`${this.title} search`}
            clearData={clearSearchTextCurry(this.field)}
          />
        );
      },
    },
  ];

  const rowSelectColumn = {
    title: '_select',
    field: '_select',
    width: 30,
    style: {
      textAlign: 'center',
      paddingRight: 0,
      paddingLeft: 0,
      backgroundColor: 'inherit',
      boxShadow: 'none',
    },
    render: ({ id, isChecked }) => (
      <input
        type="checkbox"
        className={styles.checkbox}
        checked={isChecked}
        onChange={() => handleRowSelect(id)}
      />
    ),
    renderHeader: () => (
      <input
        type="checkbox"
        className={styles.checkbox}
        checked={rows.length && rows.every(row => row.isChecked)}
        onChange={() => handleAllRowsSelect(new Set(rows.map(row => row.id)))}
      />
    ),
  };

  const rowNumberColumn = {
    title: 'Seq.',
    field: '_slNo.',
    width: 65,
    style: {
      textAlign: 'center',
      paddingRight: 0,
      paddingLeft: 0,
      borderRadius: enableRowsSelection && '5px 0 0 5px',
    },
    render: (_, index) => index || '- -',
  };

  if (showRowNumbers) {
    fixedColumns.unshift(rowNumberColumn);
  }

  if (enableRowsSelection) {
    fixedColumns.unshift(rowSelectColumn);
  }

  const optionalColumnsWithSearchHeader =
    config?.APPLICATION_VARIANT === APPLICATION_VARIANTS.IPMS4
      ? optionalColumns
      : optionalColumnsForPVWithShift.map(column => ({
          ...column,
          renderHeader: () => (
            <HeaderSearchBar
              value={searchTextMap[column.field]}
              handleChange={handleSearchTextChangeCurry(column.field)}
              title={column.title}
              placeholder={`${column.title} search`}
              clearData={clearSearchTextCurry(column.field)}
            />
          ),
          width: column.width + 20,
        }));

  const fixedColumnsStyles = {
    headerStyles: {
      color: plant.label === JLR_PLANT ? '#26662E' : '#0275A7',
      fontSize: '14px',
      fontWeight: '600',
      boxSizing: 'border-box',
    },
  };

  const optionalColumnsStyles = {
    headerStyles: {
      color: '#707070',
      backgroundColor: 'inherit',
      zIndex: 1,
      boxSizing: 'border-box',
    },
  };

  const selectedOptionalColumns = getSelectedOptionalColumns(
    optionalColumnsWithSearchHeader,
    selectedOptionalColNames,
    optionalColumnsStyles,
  );

  const fixedColumnsStyled = fixedColumns.map(column => ({
    ...column,
    headerStyles: {
      ...fixedColumnsStyles.headerStyles,
      ...column.headerStyles,
    },
  }));

  const attributesTableColumns = [
    ...fixedColumnsStyled,
    ...selectedOptionalColumns,
  ];

  useEffect(() => {
    let filteredData = sourceRows;
    const buSpecificOptionalColumns =
      config?.APPLICATION_VARIANT === APPLICATION_VARIANTS.IPMS4
        ? optionalColumns
        : optionalColumnsForPVWithShift;
    const allFieldsOfSearchExceptDescription = [
      'vc',
      ...buSpecificOptionalColumns.map(({ field }) => field),
    ];

    for (const field of allFieldsOfSearchExceptDescription) {
      filteredData = filteredData.filter(
        record =>
          !field ||
          String(record[field])
            .toLowerCase()
            .startsWith(searchTextMap[field]?.toLowerCase()),
      );
    }

    const filterByDescriptionCurry = searchText => data =>
      !searchText ||
      data.description.toLowerCase().indexOf(searchText.toLowerCase()) !== -1;
    //filtered by description
    filteredData = filteredData.filter(
      filterByDescriptionCurry(searchTextMap['description']),
    );

    setRows(filteredData);
  }, [optionalColumns, searchTextMap, sourceRows]);

  useEffect(() => {
    setStylesAccordingToPlant(plant, setStyles, defaultStyles, jlrStyles);
  }, [plant]);

  if (!sourceRows.length && renderPlaceholder) return renderPlaceholder();

  if (enableDragAndDrop)
    return (
      <VirtualizedDragAndDropList
        enableAutoHeight
        columns={attributesTableColumns}
        rows={sourceRows} // NOTE: Since there is no search, dnd list takes the sourceRows as input directly
        rowHeight={rowHeight}
        classNames={{
          tableContainerClassName: styles.attributesTable,
          tableCellClassName: styles.mTablecellDnd,
          tableHeadClassName: styles.tableHeaderDnd,
          tableRowClassName: (_, isDragging) =>
            clsx(isDragging && styles.draggingRow, styles.row),
        }}
        shouldFixRow={() => false}
        shouldDisableDragForRow={() => false}
        onDragEnd={handleDragEnd}
      />
    );

  return (
    <VirtualizedTable
      enableAutoHeight
      columns={attributesTableColumns}
      rows={rows}
      rowHeight={({ index }) => (index === 0 ? headerRowHeight : rowHeight)}
      fixedColumnCount={fixedColumns.length}
      classNames={{
        ...classNames,
        tableContainerClassName: clsx(
          styles.attributesTable,
          classNames.tableContainerClassName,
        ),
        tableCellClassName: clsx(
          styles.mTablecell,
          classNames.tableCellClassName,
        ),
      }}
      classNameBottomLeftGrid={clsx(
        styles.BottomLft,
        fixedColumns.length === attributesTableColumns.length
          ? styles.scrollBar
          : styles.noScrollBar,
      )}
      classNameBottomRightGrid={styles.scrollBar}
      classNameTopLeftGrid={styles.TopLft}
      classNameTopRightGrid={styles.TopRgt}
      {...props}
    />
  );
};

SequenceTable.propTypes = {
  classNames: PropTypes.shape({
    tableContainerClassName: PropTypes.string,
    tableClassName: PropTypes.string,
    tableHeadClassName: PropTypes.string,
    tableBodyClassName: PropTypes.string,
    tableCellClassName: PropTypes.string,
    tableRowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  }),
  enableRowsSelection: PropTypes.bool,
  handleAllRowsSelect: PropTypes.func,
  handleRowSelect: PropTypes.func,
  optionalColumns: PropTypes.shape({
    title: PropTypes.string.isRequired,
    field: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    style: PropTypes.object,
  }),
  showRowNumbers: PropTypes.bool,
  sourceRows: PropTypes.arrayOf(
    PropTypes.shape({
      vc: PropTypes.any.isRequired,
      description: PropTypes.any,
      serialNumber: PropTypes.number.isRequired,
      id: PropTypes.any.isRequired,
    }),
  ).isRequired,
  selectedOptionalColNames: PropTypes.arrayOf(PropTypes.string),
  headerRowHeight: PropTypes.number,
  rowHeight: PropTypes.number,
  renderPlaceholder: PropTypes.func,
};

export default SequenceTable;
